import type { Profile, Workspace } from 'src/services/IntentService';
import type { Stream } from 'src/services/LeadStreamsService/agent';
import type { StreamChange } from './StreamChangesDialog';

// Mirrors the existing WorkspaceView/apply_stream_profile write scope so changes
// to search are visible even when the user only edits a descriptive field.
export function streamDetailsChanges(
    stream: Stream,
    initialName: string,
    initial: Profile,
    name: string,
    profile: Profile
): StreamChange[] {
    const fields: [keyof Profile, string][] = [
        ['description', 'Описание задачи'],
        ['region', 'Локация'],
        ['website', 'Сайт'],
    ];
    const changes = [
        { label: 'Название потока', before: initialName, after: name.trim() },
        ...fields.map(([key, label]) => ({
            label,
            before: String(initial[key] || ''),
            after: String(profile[key] || ''),
        })),
    ];
    const rules = stream.filter_profile;
    const lines = (value?: string) => (value || '').split(/\r\n|\n|\r/).filter(Boolean);
    changes.push(
        {
            label: 'Ищем',
            before: rules?.target_intents?.join('\n') || '',
            after: ['Явный поиск исполнителя, товара или поставщика', ...lines(profile.criteria)].join('\n'),
        },
        {
            label: 'Исключаем',
            before: rules?.exclude?.join('\n') || '',
            after: [
                ...lines(profile.exclusions),
                'Реклама и предложения продавцов',
                'Общие обсуждения без намерения купить',
            ].join('\n'),
        },
        {
            label: 'Строгое соответствие категории',
            before:
                rules?.strict_category_match === undefined ? 'Не указано' : rules.strict_category_match ? 'Да' : 'Нет',
            after: 'Да',
        }
    );
    // A workspace may differ from the live stream: show the actual search values too.
    for (const [key, label, next] of [
        ['customer_description', 'Описание для поиска', profile.description || ''],
        ['offer', 'Предложение для поиска', profile.description || ''],
        ['geo', 'География поиска', profile.region || ''],
    ] as const) {
        const before = String(rules?.[key] || '');
        if (!changes.some((item) => item.before === before && item.after === next && item.before !== item.after))
            changes.push({ label, before, after: next });
    }
    return changes.filter(({ before, after }) => before !== after);
}

export function confirmedStreamDetails(
    result: Workspace,
    previous: Workspace,
    streamId: number,
    name: string,
    profile: Profile
) {
    return (
        result?.id === previous.id &&
        result.project_id === previous.project_id &&
        Number.isSafeInteger(result.revision) &&
        result.revision === previous.revision + 1 &&
        result.name === name &&
        result.profile != null &&
        Object.keys(profile).every(
            (key) =>
                JSON.stringify(result.profile[key as keyof Profile]) === JSON.stringify(profile[key as keyof Profile])
        ) &&
        Array.isArray(result.streams) &&
        result.streams.some((stream) => stream.id === streamId && stream.name === name)
    );
}
